import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';

const CHAPTERS = [
  {
    idx: '03.1',
    title: 'Factory layout & floor planning',
    lead: 'Describe it in plain language — Emboscale generates the factory layout as a dimensioned 3D twin.',
    image: '/images/product/digital-twin.webp',
    alt: '3D factory layout with the agent plan and readiness findings',
    points: [
      'Stations, machines, conveyors, walls, fencing, safety zones — dimensioned, not decorative',
      'CAD ingest: upload STEP assemblies, auto-extract the BOM, place real part meshes into station scenes',
      'Layout feasibility & safety audits catch overlapping equipment, unfenced robots, blocked clearances',
      'DXF floor-plan export, Excel import, full manifest editing via chat',
    ],
    link: { to: '/capabilities/factory-layout-planning/', text: 'Factory layout software compared' },
  },
  {
    idx: '03.2',
    title: 'Planning, scheduling & simulation',
    lead: 'Deterministic math, not LLM guesses.',
    image: '/images/product/planning.webp',
    alt: 'Cell layout beside a shift plan with takt-time analysis',
    points: [
      'Shift schedules from a CP-SAT solver — a Gantt with due-date-risk flagging',
      'Line balancing across stations with cycle-time rollups',
      'Discrete-event throughput simulation: takt, utilization, bottlenecks per station',
      'A readiness gate that says exactly what blocks production, and why',
    ],
    link: { to: '/capabilities/line-balancing/', text: 'How line balancing works' },
  },
  {
    idx: '03.3',
    title: '3D work instructions',
    lead: 'From a STEP file to instructions your operators can follow.',
    image: '/images/product/instructions.webp',
    alt: 'Step-by-step 3D assembly instruction with verification checks and sign-off',
    points: [
      'Assembly sequence proposed, confirmed, and turned into step-by-step digital work instructions in 3D',
      'Every fastening step cites real torque specs, part by part',
      'Full-line playback: parallel stations, converging sub-assemblies, per-station counters',
      'Human sign-off built into every step',
    ],
  },
  {
    idx: '03.4',
    title: 'Agentic robot programming',
    lead: 'Offline robot programming, without the programming.',
    image: '/images/product/robot-program.webp',
    alt: 'Six-axis robot executing an agent-authored pick-and-place program with trajectory preview',
    points: [
      'Pick-and-place, palletizing patterns, multi-point screwdriving, welding',
      'Industry-standard 6-axis robot models with datasheet kinematics and real tool offsets',
      'Tool selection per part by width, mass, and payload; obstacle-aware transfer routing',
      'Multi-strategy authoring — 3 candidates compared, 27% faster after optimization, every variant re-verified',
    ],
  },
  {
    idx: '03.5',
    title: 'One agent, your rules',
    lead: 'Your process knowledge compounds instead of evaporating.',
    image: '/images/product/skills.webp',
    alt: "Factory scene beside the agent's learned skills and applied patches",
    points: [
      'One conversation drives the 3D canvas; every number quoted is the verified number, verbatim',
      'Correct the agent once and it persists as a learned skill applied to every future plan',
      'Upload your standards; the agent elicits what’s missing and cites them in outputs',
      'Approvals gate consequential changes; every edit carries actor attribution',
    ],
  },
];

const Chapters = () => (
  <section id="capabilities" className="py-16 bg-white dark:bg-black">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-32">
      {CHAPTERS.map((c, i) => (
        <div
          key={c.idx}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
            i % 2 === 1 ? 'lg:[direction:rtl]' : ''
          }`}
        >
          <Reveal className="lg:[direction:ltr]">
            <div className="font-mono text-xs tracking-[0.25em] uppercase text-emerald-600 dark:text-emerald-400 mb-4">{c.idx}</div>
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">{c.title}</h3>
            <p className="text-lg text-gray-700 dark:text-gray-300 font-medium mb-8">{c.lead}</p>
            <ul className="space-y-4">
              {c.points.map((p) => (
                <li key={p} className="flex gap-4 text-gray-600 dark:text-gray-400 leading-relaxed">
                  <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 flex-shrink-0"></span>
                  {p}
                </li>
              ))}
            </ul>
            {c.link && (
              <Link to={c.link.to} className="inline-block mt-8 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium">
                {c.link.text} →
              </Link>
            )}
          </Reveal>
          <Reveal delay={120} className="lg:[direction:ltr]">
            <div className="rounded-xl overflow-hidden border border-black/10 dark:border-white/10 shadow-2xl shadow-black/20 dark:shadow-black/60 bg-gray-950">
              <img src={c.image} alt={c.alt} loading="lazy" className="w-full block" />
            </div>
          </Reveal>
        </div>
      ))}
    </div>
  </section>
);

export default Chapters;
